import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { AnalyticsAdminService } from '@api-client';
import dayjs from 'dayjs';
import {
  ChartComponent,
  ChartOptions,
} from 'libs/client-libs/src/components/chart/chart.component';
import { catchError, defer, of } from 'rxjs';

@Component({
  selector: 'app-dashboard-analytics',
  standalone: true,
  imports: [CommonModule, ChartComponent],
  templateUrl: './analytics.component.html',
})
export class AnalyticsComponent {
  private readonly analyticsService = inject(AnalyticsAdminService);

  protected readonly selectedStartDate = signal(
    dayjs().subtract(29, 'day').format('YYYY-MM-DD')
  );
  protected readonly selectedEndDate = signal(dayjs().format('YYYY-MM-DD'));
  protected readonly customStartDate = signal(this.selectedStartDate());
  protected readonly customEndDate = signal(this.selectedEndDate());
  protected readonly error = signal(false);

  private readonly analyticsResource = rxResource({
    params: () => ({
      startDate: this.selectedStartDate(),
      endDate: this.selectedEndDate(),
    }),
    stream: ({ params }) =>
      defer(() => {
        this.error.set(false);
        return this.analyticsService.analyticsAdminControllerGetSummaryV1(
          params
        );
      }).pipe(
        catchError(() => {
          this.error.set(true);
          return of(undefined);
        })
      ),
  });

  protected readonly analytics = computed(() => this.analyticsResource.value());
  protected readonly loading = computed(() =>
    this.analyticsResource.isLoading()
  );
  protected readonly chartOptions = computed<ChartOptions>(() => {
    const daily = this.analytics()?.daily ?? [];
    const hasSingleValue = daily.length === 1;

    return {
      chart: {
        type: hasSingleValue ? 'bar' : 'line',
        height: 280,
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: 'inherit',
      },
      colors: ['#2b7fff'],
      dataLabels: { enabled: false },
      markers: { size: hasSingleValue ? 0 : 5 },
      plotOptions: {
        bar: {
          columnWidth: '36%',
          borderRadius: 4,
        },
      },
      stroke: { curve: 'smooth', width: hasSingleValue ? 0 : 3 },
      series: [
        {
          name: '활성 사용자',
          data: daily.map(item => item.activeUsers),
        },
      ],
      xaxis: {
        categories: daily.map(item => item.date),
      },
      yaxis: { min: 0, forceNiceScale: true },
    };
  });

  protected selectPreset(days: number): void {
    const endDate = dayjs().format('YYYY-MM-DD');
    const startDate = dayjs()
      .subtract(days - 1, 'day')
      .format('YYYY-MM-DD');
    this.customStartDate.set(startDate);
    this.customEndDate.set(endDate);
    this.selectedStartDate.set(startDate);
    this.selectedEndDate.set(endDate);
  }

  protected updateCustomStartDate(event: Event): void {
    this.customStartDate.set((event.target as HTMLInputElement).value);
  }

  protected updateCustomEndDate(event: Event): void {
    this.customEndDate.set((event.target as HTMLInputElement).value);
  }

  protected applyCustomRange(): void {
    if (
      !this.customStartDate() ||
      !this.customEndDate() ||
      this.customStartDate() > this.customEndDate()
    ) {
      return;
    }
    this.selectedStartDate.set(this.customStartDate());
    this.selectedEndDate.set(this.customEndDate());
  }

  protected retry(): void {
    this.analyticsResource.reload();
  }

  protected formatNumber(value: number): string {
    return value.toLocaleString('ko-KR');
  }
}
